import { FC, useCallback, useEffect, useState } from "react";
import { ListUtil } from "src/framework/commons/ListUtil";
import { ValidateUtil } from "src/framework/commons/ValidateUtil";
import { InputCEP } from "src/framework/components/forms/inputs/InputCEP";
import { InputPhone } from "src/framework/components/forms/inputs/InputPhone";
import api, { STATUS_CODE } from "src/framework/providers/api";
import { useObservable } from "src/framework/store/storeUtil";
import { cidades$, estados$ } from "src/store/global.store";
import { Cidade } from "src/components/geral/RegioesDataTable/types";
import { Tooltip } from "@mui/material";
import { notifierStore } from "src/framework/store/NotifierStore";
import { InputText } from "src/framework/components/forms/inputs/InputText";
import { InputCPFCNPJ } from "src/framework/components/forms/inputs/InputCPFCNPJ";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";
import { parseCEP } from "src/framework/commons/masks";
import { NOME_CLIENTE_MAX_LENGTH } from "src/components/projetos/constants";
import { useProjetoPersonalizadoOffgridStore } from "src/store/vendas/projetos/personalizado_offgrid/personalizadoOffgrid.store";
import { Projeto } from "src/components/projetos/types";

interface FormClientProperties {
    errorState: string[];
    onChangeError: (errorState: any) => void
}

const CLIENTE_PROPOSTA = "cliente_proposta"
const EMAIL_PROPOSTA = "email_proposta"
const CIDADE_ID = "cidade_id"

const FormCliente: FC<FormClientProperties> = ({ errorState, onChangeError }) => {
    const projeto = useProjetoPersonalizadoOffgridStore(state => state.state.data)
    const { changeValue } = useProjetoPersonalizadoOffgridStore(state => state.actions)
    const estadoListState = useObservable(estados$);
    const cidadeListState = useObservable(cidades$);

    const [estadoSelecionado, setEstadoSelecionado] = useState<string>("")

    useEffect(()=>{
        if (projeto?.estado) {
            setEstadoSelecionado(projeto.estado.id)
        }
    }, [projeto?.estado])

    const validateEmail = (email: string) => {
        return !!String(email)
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          );
    };

    const requestCep = useCallback(async (state: Projeto) => {
        const formattedValue = state.cep?.replace(/\D/g, "") || "";

        const values: Partial<Projeto> = {};

        if (formattedValue.length < 8) {
            values.cep_encontrado = false;
        }

        if (formattedValue.length === 8) {
            const result = await api.get({ url: `/cep/${formattedValue}` });

            if (result.statusCode !== STATUS_CODE.OK) {
                Object.assign(values, {
                    estado: null,
                    cidade: null,
                    endereco_proposta: "",
                    bairro: "",
                    cep_encontrado: false,
                    cep: "",
                });

                setEstadoSelecionado("");

                notifierStore.setNotifier({
                    title: "Aviso",
                    content: "CEP não cadastrado. Preencha os campos do endereço manualmente.",
                    timeOut: 3500,
                    onClose: () => {
                    notifierStore.setOpen(false);
                    },
                });

                notifierStore.setOpen(true);
            } else {
                const { data } = result;
                const estado = estadoListState?.data?.find((estado: any) => estado.id === data.uf);
                let cidade = cidadeListState?.data?.find((cidade: Cidade) => cidade.id === data.ibge);

                if (!cidade && data) {
                    cidade = {
                        id: data.ibge,
                        nome: data.cidade,
                        estado_id: data.uf,
                    };
                }

                Object.assign(values, {
                    cep_encontrado: !!estado,
                    estado,
                    cidade,
                    bairro: data.bairro || "",
                    endereco_proposta: data.endereco || "",
                    numero: "",
                });

                if (estado) {
                    const isTrocaUf = estado.id !== state?.estado_original?.id;

                    Object.assign(values, {
                        isTrocaUf,
                        tarifa_concessionaria_id: state.tarifa_concessionaria_id_original,
                        preco_kwh_com_imposto: state.preco_kwh_com_imposto_original,
                    });
                }

                setEstadoSelecionado(data?.uf);
            }
        }

        changeValue(values);
    }, [changeValue, cidadeListState, estadoListState]);

    return (
        <>
            <div className="row">
                <div className="L12">
                    <InputText label="Título do projeto" fullWidth
                        value={projeto?.titulo}
                        defaultValue={projeto?.titulo}
                        maxLength={100}
                        onChange={(e: any) => {
                            changeValue({titulo: e.target.value})
                        }}
                    />
                </div>
                <div className="X12 L4">
                    <InputText label="* Nome do cliente" fullWidth
                        value={projeto?.cliente_proposta}
                        error={errorState.includes(CLIENTE_PROPOSTA)}
                        maxLength={NOME_CLIENTE_MAX_LENGTH}
                        defaultValue={projeto?.cliente_proposta}
                        onChange={(e: any) => {
                            changeValue({cliente_proposta: e.target.value})
                        }}
                        onKeyUp={(e: any) => {
                            if (e.target.value !== "") {
                                const idxError = errorState.findIndex((es) => es === CLIENTE_PROPOSTA)
                                if (idxError > -1) {
                                    errorState.splice(idxError, 1)
                                    onChangeError(errorState)
                                }
                            }
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputText label="E-mail" fullWidth
                        value={projeto?.email_proposta}
                        error={errorState.includes(EMAIL_PROPOSTA)}
                        helperText={errorState.includes(EMAIL_PROPOSTA) ? "E-mail inválido" : ''}
                        inputProps={{
                            autocomplete: 'off',
                        }}
                        onChange={(e: any) => {
                            changeValue({ email_proposta: (String(e.target.value) || '').trim() })
                        }}
                        onKeyDown={(e: any) => {
                            const spaceRegex = /\s/g
                            e.target.value = e.target.value.replace(spaceRegex, '')
                        }}
                        onKeyUp={(e: any) => {
                            let _errorState = [...errorState]
                            if (e.target.value !== "") {
                                _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                                if (ValidateUtil.isValidEmail(e.target.value) && validateEmail(e.target.value)) {
                                    _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                                } else {
                                    _errorState = ListUtil.addItem(_errorState, EMAIL_PROPOSTA)
                                }
                            }else{
                                _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                            }
                            onChangeError(_errorState)
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputCPFCNPJ
                        label="CPF/CNPJ"
                        maxLength={18}
                        value={projeto?.cpf_cnpj_proposta}
                        onChangeValue={(newValue) => {
                            const { value } = newValue;
                            changeValue({cpf_cnpj_proposta: value})
                        }}
                    />
                </div>
                <div className="X12 L4">
                    <InputCEP
                        label="CEP"
                        value={projeto.cep || ''}
                        onChange={async (e: any) => {
                            const { value = "" } = e.target;

                            const values = value
                                ? { cep: parseCEP(value) }
                                : {
                                    cep: "",
                                    endereco_proposta: "",
                                    numero: "",
                                    bairro: "",
                                };

                            changeValue(values);

                            requestCep({
                                ...projeto,
                                ...values,
                            });
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputComboBoxList
                        id={`listagem-personalizado-off-estado-cb`}
                        key={`listagem-personalizado-off-estado-cb`}
                        label={"* UF"}
                        disabled={projeto.cep_encontrado}
                        textField="nome"
                        valueField="id"
                        options={estadoListState?.data || []}
                        value={projeto?.estado || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option.id} - ${option.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option.id} - ${option.nome}`
                        }}
                        onChange={(value: any) => {
                            if (value) {
                                const isTrocaUf = value.id !== projeto?.estado_original?.id;

                                changeValue({
                                    isTrocaUf,
                                    tarifa_concessionaria_id: Number(projeto?.tarifa_concessionaria_id_original),
                                    preco_kwh_com_imposto: Number(projeto?.preco_kwh_com_imposto_original),
                                    estado: value,
                                    cidade: undefined,
                                    cidade_id: undefined,
                                });

                                setEstadoSelecionado(String(value.id));
                            } else {
                                changeValue({
                                    estado: undefined,
                                });

                                setEstadoSelecionado('');
                            }
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputComboBoxList
                        id={`listagem-personalizado-off-cidade-cb`}
                        key={`listagem-personalizado-off-cidade-cb`}
                        disabled={projeto.cep_encontrado || estadoSelecionado === ""}
                        label={"* Cidade"}
                        textField="nome"
                        valueField="id"
                        error={errorState.includes(CIDADE_ID)}
                        options={cidadeListState?.data?.filter((c: Cidade) => c.estado_id === estadoSelecionado) || []}
                        value={projeto?.cidade || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option?.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option?.nome}`
                        }}
                        onChange={(value: Cidade) => {
                            if (value) {
                                const idxError = errorState.findIndex((es) => es === CIDADE_ID);

                                if (idxError > -1) {
                                    errorState.splice(idxError, 1);
                                    onChangeError([...errorState]);
                                }

                                changeValue({
                                    cidade: value,
                                    cidade_id: value.id,
                                });
                            } else {
                                changeValue({
                                    cidade: null,
                                    cidade_id: undefined,
                                });
                            }
                        }}
                    />
                </div>

                <div className="X12">
                    <InputText label="Rua"
                        fullWidth
                        value={projeto?.endereco_proposta}
                        onChange={(e: any) => {
                        const endereco = e.target.value;

                        changeValue({
                            endereco_proposta: endereco,
                            ...(!endereco.trim() && {
                            bairro: "",
                            numero: "",
                            }),
                        });
                        }}
                    />
                </div>
                <div className="X12 L4">
                    <InputText
                        disabled={!projeto?.endereco_proposta?.trim()}
                        fullWidth
                        maxLength={10}
                        label="Número"
                        name="numero"
                        onChange={(e: any) => {
                            changeValue({numero: e.target.value})
                        }}
                        value={projeto.numero || ""}
                    />
                </div>

                <div className="X12 L4">
                    <InputText label="Bairro"
                        disabled={!projeto?.endereco_proposta?.trim()}
                        fullWidth
                        value={projeto?.bairro || ""}
                        onChange={(e: any) => {
                            changeValue({ bairro: e.target.value })
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputPhone
                        label="Telefone"
                        value={projeto?.telefone_proposta}
                        onChangeValue={(newValue) => {
                            const { value } = newValue;
                            changeValue({ telefone_proposta: value })
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export { FormCliente }
